import React, { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Search } from 'lucide-react';
import Button from '../../../shared/components/ui/Button';
import Modal from '../../../shared/components/ui/Modal';
import {
  EmptyRow,
  StatusPill,
  Table,
  TBody,
  Td,
  Th,
  THead,
  Tr
} from '../../../shared/components/ui/primitives';
import useEmailLeadSettings from '../../hooks/useEmailLeadSettings';
import {
  getEmailLeadMessage,
  getEmailLeadMessages,
  getPreLeadLists,
  reprocessEmailLeadMessage,
  resolveEmailLeadMessage
} from '../../services/api/implementation/impl';
import {
  EMAIL_LEAD_STATUSES,
  emailLeadMessageQueryKey,
  emailLeadMessagesQueryKey,
  emailLeadStatusMeta,
  formatContactPhone
} from '../../utils/emailLeadsAccess';

/**
 * Every portal email we read, and what became of it.
 *
 * Deliberately not "a list of leads". The leads themselves live in the pre-lead
 * lists where the rest of the sales flow already works; what this table adds is
 * the answer to the question that otherwise has none — "a 99acres mail arrived
 * at 4pm and it is not in the CRM, why" — which needs the emails we decided
 * *not* to ingest to be just as visible as the ones we did.
 *
 * So the default filter is "needs review": the only status that is asking the
 * user for something, and the one that turns into a lead with a single click.
 */

const PAGE_SIZE = 25;

function errorText(err, fallback) {
  return err?.response?.data?.message || err?.message || fallback;
}

const STATUS_TONE = { green: 'success', amber: 'warning', red: 'danger', gray: 'neutral' };

function StatusChip({ status }) {
  const meta = emailLeadStatusMeta(status);
  return (
    <span title={meta.hint}>
      <StatusPill tone={STATUS_TONE[meta.tone] ?? 'neutral'} dense>
        {meta.label}
      </StatusPill>
    </span>
  );
}

/** The payoff for never silently dropping a low-confidence extraction. */
function ResolveModal({ message, preLeadLists, onClose, onResolved }) {
  const [name, setName] = useState(message.contactName ?? '');
  const [phone, setPhone] = useState(formatContactPhone(message) ?? '');
  const [email, setEmail] = useState(message.contactEmail ?? '');
  const [listId, setListId] = useState('');

  const mutation = useMutation({
    mutationFn: () =>
      resolveEmailLeadMessage(message.id, {
        name: name.trim() || null,
        phone: phone.trim(),
        email: email.trim() || null,
        pre_lead_list_id: listId ? Number(listId) : null
      }),
    onSuccess: (result) => {
      toast.success(result?.created ? 'Lead created' : 'Added to an existing lead');
      onResolved();
    },
    onError: (err) => toast.error(errorText(err, 'Could not create the lead'))
  });

  return (
    <Modal isDirty confirmOnClose name="Create lead" onClose={onClose} width={45}>
      <div className="space-y-4 p-1">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Create lead from this email</h2>
          <p className="text-sm text-gray-600">
            {message.reason ?? 'Fill in what is missing and this becomes a pre-lead.'}
          </p>
        </div>

        <div className="rounded-xl bg-gray-50 px-3 py-2 text-xs text-gray-600">
          <p className="font-medium text-gray-900">{message.subject ?? '(no subject)'}</p>
          <p className="mt-0.5">
            {message.fromAddress} · {message.portalLabel}
          </p>
        </div>

        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase text-gray-500">Name</label>
            <input
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase text-gray-500">
              Mobile
            </label>
            <input
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
              value={phone}
              placeholder="10-digit mobile, or +971… for an overseas buyer"
              onChange={(e) => setPhone(e.target.value)}
            />
          </div>
        </div>

        <div>
          <label className="mb-1 block text-xs font-semibold uppercase text-gray-500">
            Email (optional)
          </label>
          <input
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>

        <div>
          <label className="mb-1 block text-xs font-semibold uppercase text-gray-500">
            Pre-lead list
          </label>
          <select
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
            value={listId}
            onChange={(e) => setListId(e.target.value)}>
            <option value="">Use the list this email was routed to</option>
            {preLeadLists.map((list) => (
              <option key={list.id} value={list.id}>
                {list.name}
              </option>
            ))}
          </select>
        </div>

        <div className="flex gap-2">
          <Button
            displayType="primary"
            disabled={!phone.trim() || mutation.isPending}
            isLoading={mutation.isPending}
            onClick={() => mutation.mutate()}>
            Create lead
          </Button>
          <Button displayType="secondary" onClick={onClose}>
            Cancel
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function DetailModal({ messageId, onClose, onResolve }) {
  const { data, isLoading } = useQuery({
    queryKey: emailLeadMessageQueryKey(messageId),
    queryFn: () => getEmailLeadMessage(messageId),
    retry: false
  });

  return (
    <Modal name="Email" onClose={onClose} width={55}>
      <div className="space-y-4 p-1">
        {isLoading || !data ? (
          <p className="text-sm text-gray-500">Loading…</p>
        ) : (
          <>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                {data.subject ?? '(no subject)'}
              </h2>
              <p className="mt-1 text-sm text-gray-600">
                {data.fromName ? `${data.fromName} · ` : ''}
                {data.fromAddress} · {data.portalLabel}
                {data.receivedAt ? ` · ${new Date(data.receivedAt).toLocaleString()}` : ''}
              </p>
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <StatusChip status={data.status} />
                {data.method ? (
                  <span className="text-xs text-gray-500">
                    read by {data.method === 'llm' ? 'AI' : data.method}
                    {data.confidence != null
                      ? ` · confidence ${Math.round(data.confidence * 100)}%`
                      : ''}
                  </span>
                ) : null}
              </div>
            </div>

            {data.reason ? (
              <p className="rounded-xl bg-amber-50 px-4 py-3 text-xs text-amber-900">
                {data.reason}
              </p>
            ) : null}

            {data.contactPhone || data.contactName || data.contactEmail ? (
              <div className="rounded-xl border border-gray-200 px-4 py-3 text-sm">
                <p className="text-xs font-semibold uppercase text-gray-500">Contact found</p>
                <p className="mt-1 text-gray-900">
                  {data.contactName ?? '—'} · {formatContactPhone(data) ?? 'no number'}
                </p>
                {data.contactEmail ? (
                  <p className="text-xs text-gray-600">{data.contactEmail}</p>
                ) : null}
              </div>
            ) : null}

            {data.fields?.length ? (
              <div>
                <p className="mb-1 text-xs font-semibold uppercase text-gray-500">
                  Details from the portal
                </p>
                <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                  {data.fields.map((field) => (
                    <div
                      key={`${field.label}-${field.value}`}
                      className="rounded-lg bg-gray-50 px-3 py-2">
                      <p className="text-[10px] uppercase text-gray-500">{field.label}</p>
                      <p className="text-sm text-gray-900">{field.value}</p>
                    </div>
                  ))}
                </div>
              </div>
            ) : null}

            {data.bodyExcerpt ? (
              <div>
                <p className="mb-1 text-xs font-semibold uppercase text-gray-500">Email content</p>
                <pre className="max-h-64 overflow-y-auto whitespace-pre-wrap rounded-lg bg-gray-50 px-3 py-2 text-xs text-gray-700">
                  {data.bodyExcerpt}
                </pre>
              </div>
            ) : null}

            <div className="flex gap-2">
              {data.status === 'needs_review' && !data.preLeadId ? (
                <Button displayType="primary" onClick={() => onResolve(data)}>
                  Create lead from this
                </Button>
              ) : null}
              <Button displayType="secondary" onClick={onClose}>
                Close
              </Button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}

export default function PortalLeadsTable({ enabled = true }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [status, setStatus] = useState('needs_review');
  const [search, setSearch] = useState('');
  const [portal, setPortal] = useState('');
  const [page, setPage] = useState(1);
  const { portals } = useEmailLeadSettings();
  const [detailId, setDetailId] = useState(null);
  const [resolving, setResolving] = useState(null);

  const params = useMemo(
    () => ({
      status: status || undefined,
      portal: portal || undefined,
      search: search || undefined,
      page,
      limit: PAGE_SIZE
    }),
    [status, portal, search, page]
  );

  const { data, isLoading, isError } = useQuery({
    queryKey: emailLeadMessagesQueryKey(params),
    queryFn: () => getEmailLeadMessages(params),
    retry: false,
    enabled
  });

  const rows = data?.rows ?? [];
  const counts = data?.counts ?? {};
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const refreshList = () => {
    queryClient.invalidateQueries({ queryKey: ['emailLeadMessages'] });
  };

  const reprocessMutation = useMutation({
    mutationFn: (messageId) => reprocessEmailLeadMessage(messageId),
    onSuccess: (result) => {
      toast.success(
        result?.outcome === 'created'
          ? 'Lead created'
          : `Re-read — ${result?.reason ?? result?.outcome ?? 'no change'}`
      );
      refreshList();
    },
    onError: (err) => toast.error(errorText(err, 'Could not re-process this email'))
  });

  const { data: preLeadListData } = useQuery({
    queryKey: ['preLeadLists'],
    queryFn: getPreLeadLists,
    retry: false
  });

  const statusTabs = [
    { value: '', label: 'All', count: Object.values(counts).reduce((sum, n) => sum + (n || 0), 0) },
    ...EMAIL_LEAD_STATUSES.filter((entry) => entry.value !== 'pending' || counts.pending).map(
      (entry) => ({ ...entry, count: counts[entry.value] ?? 0 })
    )
  ];

  return (
    <section className="space-y-3" aria-labelledby="portal-leads-heading">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h3 id="portal-leads-heading" className="text-base font-semibold text-gray-900">
            Portal leads
          </h3>
          <p className="text-xs text-gray-500">
            Every enquiry read from your inboxes, and where it went.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <label className="relative block">
            <span className="sr-only">Search portal leads</span>
            <Search
              className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
              aria-hidden="true"
            />
            <input
              type="search"
              className="w-72 rounded-control border border-hairline-strong bg-surface py-2 pl-9 pr-3 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-600"
              placeholder="Search name, number, subject"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
            />
          </label>
          {portals.length ? (
            <select
              aria-label="Filter by portal"
              className="rounded-control border border-hairline-strong bg-surface px-3 py-2 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-600"
              value={portal}
              onChange={(e) => {
                setPortal(e.target.value);
                setPage(1);
              }}>
              <option value="">All portals</option>
              {portals.map((entry) => (
                <option key={entry.value} value={entry.value}>
                  {entry.label}
                </option>
              ))}
            </select>
          ) : null}
        </div>
      </div>

      <div className="overflow-hidden rounded-panel border border-hairline bg-surface shadow-card">
        <div
          role="tablist"
          aria-label="Filter by status"
          className="flex gap-1 overflow-x-auto border-b border-hairline px-3">
          {statusTabs.map((entry) => {
            const selected = status === entry.value;
            return (
              <button
                key={entry.value || 'all'}
                type="button"
                role="tab"
                aria-selected={selected}
                title={entry.hint}
                onClick={() => {
                  setStatus(entry.value);
                  setPage(1);
                }}
                className={`-mb-px flex shrink-0 items-center gap-2 border-b-2 px-3 py-3 text-sm transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-600 ${
                  selected
                    ? 'border-brand-700 font-semibold text-gray-900'
                    : 'border-transparent text-gray-500 hover:text-gray-800'
                }`}>
                {entry.label}
                <span className="rounded-pill bg-surface-sunken px-2 py-0.5 text-xs font-semibold text-gray-600">
                  {entry.count}
                </span>
              </button>
            );
          })}
        </div>

        <div className="overflow-x-auto">
          <Table>
            <THead>
              <tr>
                <Th>Contact</Th>
                <Th>Portal</Th>
                <Th>Subject</Th>
                <Th>Received</Th>
                <Th>Status</Th>
                <Th align="right">
                  <span className="sr-only">Actions</span>
                </Th>
              </tr>
            </THead>
            <TBody>
              {isLoading ? (
                <EmptyRow colSpan={6}>Loading…</EmptyRow>
              ) : isError ? (
                <EmptyRow colSpan={6}>Could not load these emails. Try again in a moment.</EmptyRow>
              ) : rows.length === 0 ? (
                <EmptyRow colSpan={6}>
                  {status === 'needs_review'
                    ? 'Nothing needs your attention — every enquiry was read.'
                    : 'No emails match these filters.'}
                </EmptyRow>
              ) : (
                rows.map((row) => (
                  <Tr key={row.id}>
                    <Td>
                      <p className="font-medium text-gray-900">{row.contactName ?? '—'}</p>
                      <p className="text-xs tabular-nums text-gray-500">
                        {formatContactPhone(row) ?? row.contactEmail ?? 'No contact found'}
                      </p>
                    </Td>
                    <Td>
                      <StatusPill tone="neutral" dense>
                        {row.portalLabel}
                      </StatusPill>
                    </Td>
                    <Td className="max-w-xs truncate text-gray-700">{row.subject ?? '—'}</Td>
                    <Td className="whitespace-nowrap text-xs tabular-nums text-gray-500">
                      {new Date(row.receivedAt ?? row.createdAt).toLocaleString()}
                    </Td>
                    <Td>
                      <StatusChip status={row.status} />
                    </Td>
                    <Td align="right">
                      <div className="flex justify-end gap-1">
                        {row.preLeadId ? (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() =>
                              navigate(
                                row.preLeadListId
                                  ? `/builder/marketing/prelead-lists/${row.preLeadListId}`
                                  : '/builder/marketing/prelead-lists'
                              )
                            }>
                            View lead
                          </Button>
                        ) : null}
                        {row.status === 'needs_review' ? (
                          <Button variant="ghost" size="sm" onClick={() => setResolving(row)}>
                            Create lead
                          </Button>
                        ) : null}
                        {row.status === 'skipped' || row.status === 'failed' ? (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => reprocessMutation.mutate(row.id)}>
                            Re-read
                          </Button>
                        ) : null}
                        <Button variant="secondary" size="sm" onClick={() => setDetailId(row.id)}>
                          Open
                        </Button>
                      </div>
                    </Td>
                  </Tr>
                ))
              )}
            </TBody>
          </Table>
        </div>
      </div>

      {totalPages > 1 ? (
        <div className="flex items-center justify-end gap-2">
          <Button
            variant="secondary"
            size="sm"
            disabled={page <= 1}
            onClick={() => setPage((current) => Math.max(1, current - 1))}>
            Previous
          </Button>
          <span className="text-xs text-gray-600">
            Page {page} of {totalPages}
          </span>
          <Button
            variant="secondary"
            size="sm"
            disabled={page >= totalPages}
            onClick={() => setPage((current) => Math.min(totalPages, current + 1))}>
            Next
          </Button>
        </div>
      ) : null}

      {detailId ? (
        <DetailModal
          messageId={detailId}
          onClose={() => setDetailId(null)}
          onResolve={(message) => {
            setDetailId(null);
            setResolving(message);
          }}
        />
      ) : null}

      {resolving ? (
        <ResolveModal
          message={resolving}
          preLeadLists={preLeadListData?.preLeadList ?? []}
          onClose={() => setResolving(null)}
          onResolved={() => {
            setResolving(null);
            refreshList();
          }}
        />
      ) : null}
    </section>
  );
}
